import { useState, useEffect, useRef } from 'react';
import {
  DocumentTextIcon,
  BanknotesIcon,
  PlusIcon,
  PrinterIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { BankInformation } from '../types/settings';

interface Invoice {
  id: string;
  invoice_number: string;
  amount: number;
  currency: string;
  description: string;
  due_date: string | null;
  status: string;
  issued_date: string;
  notes: string;
  created_at: string;
}

interface Payment {
  id: string;
  invoice_id: string | null;
  amount: number;
  currency: string;
  payment_date: string;
  payment_method: string;
  reference_number: string;
  receipt_number: string;
  notes: string;
  created_at: string;
}

interface PartnerFinanceTabProps {
  partnerId: string;
  partnerCompany: string;
  partnerTier: string;
  contributionAmount: number;
}

export default function PartnerFinanceTab({
  partnerId,
  partnerCompany,
  partnerTier,
  contributionAmount,
}: PartnerFinanceTabProps) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [bankInfo, setBankInfo] = useState<BankInformation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewReceipt, setPreviewReceipt] = useState<Payment | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const [invoiceForm, setInvoiceForm] = useState({
    amount: contributionAmount || 0,
    description: `${partnerTier} Sponsorship - ${partnerCompany}`,
    due_date: '',
    notes: '',
    bank_account_id: '',
  });

  const [paymentForm, setPaymentForm] = useState({
    invoice_id: '',
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: '',
  });

  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);

  useEffect(() => {
    fetchData();
  }, [partnerId]);

  const setupTables = async () => {
    setSettingUp(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Not authenticated'); return; }

      const sql = `
        CREATE TABLE IF NOT EXISTS partner_invoices (id UUID PRIMARY KEY DEFAULT gen_random_uuid(), partner_id UUID NOT NULL REFERENCES partners(id) ON DELETE CASCADE, invoice_number TEXT UNIQUE NOT NULL, amount NUMERIC(12,2) NOT NULL DEFAULT 0, currency TEXT NOT NULL DEFAULT 'NGN', description TEXT NOT NULL DEFAULT '', due_date DATE, status TEXT NOT NULL DEFAULT 'draft', issued_date DATE NOT NULL DEFAULT CURRENT_DATE, notes TEXT DEFAULT '', created_by UUID REFERENCES auth.users(id), created_at TIMESTAMPTZ DEFAULT now(), updated_at TIMESTAMPTZ DEFAULT now());
        ALTER TABLE partner_invoices ENABLE ROW LEVEL SECURITY;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_invoices' AND policyname='Auth can view partner invoices') THEN CREATE POLICY "Auth can view partner invoices" ON partner_invoices FOR SELECT TO authenticated USING (true); END IF; END $$;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_invoices' AND policyname='Auth can create partner invoices') THEN CREATE POLICY "Auth can create partner invoices" ON partner_invoices FOR INSERT TO authenticated WITH CHECK (true); END IF; END $$;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_invoices' AND policyname='Auth can update partner invoices') THEN CREATE POLICY "Auth can update partner invoices" ON partner_invoices FOR UPDATE TO authenticated USING (true) WITH CHECK (true); END IF; END $$;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_invoices' AND policyname='Auth can delete partner invoices') THEN CREATE POLICY "Auth can delete partner invoices" ON partner_invoices FOR DELETE TO authenticated USING (true); END IF; END $$;
        CREATE TABLE IF NOT EXISTS partner_payments (id UUID PRIMARY KEY DEFAULT gen_random_uuid(), partner_id UUID NOT NULL REFERENCES partners(id) ON DELETE CASCADE, invoice_id UUID REFERENCES partner_invoices(id) ON DELETE SET NULL, amount NUMERIC(12,2) NOT NULL DEFAULT 0, currency TEXT NOT NULL DEFAULT 'NGN', payment_date DATE NOT NULL DEFAULT CURRENT_DATE, payment_method TEXT NOT NULL DEFAULT 'bank_transfer', reference_number TEXT DEFAULT '', receipt_number TEXT UNIQUE NOT NULL, notes TEXT DEFAULT '', created_by UUID REFERENCES auth.users(id), created_at TIMESTAMPTZ DEFAULT now(), updated_at TIMESTAMPTZ DEFAULT now());
        ALTER TABLE partner_payments ENABLE ROW LEVEL SECURITY;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_payments' AND policyname='Auth can view partner payments') THEN CREATE POLICY "Auth can view partner payments" ON partner_payments FOR SELECT TO authenticated USING (true); END IF; END $$;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_payments' AND policyname='Auth can create partner payments') THEN CREATE POLICY "Auth can create partner payments" ON partner_payments FOR INSERT TO authenticated WITH CHECK (true); END IF; END $$;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_payments' AND policyname='Auth can update partner payments') THEN CREATE POLICY "Auth can update partner payments" ON partner_payments FOR UPDATE TO authenticated USING (true) WITH CHECK (true); END IF; END $$;
        DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='partner_payments' AND policyname='Auth can delete partner payments') THEN CREATE POLICY "Auth can delete partner payments" ON partner_payments FOR DELETE TO authenticated USING (true); END IF; END $$;
      `;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-migration`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ sql }),
        }
      );
      const result = await res.json();
      if (result.success) {
        toast.success('Finance tables created');
        setNeedsSetup(false);
        fetchData();
      } else {
        toast.error(result.error || 'Failed to setup tables');
      }
    } catch (err: any) {
      toast.error(err.message || 'Setup failed');
    } finally {
      setSettingUp(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, paymentsRes, bankRes] = await Promise.all([
        supabase
          .from('partner_invoices')
          .select('*')
          .eq('partner_id', partnerId)
          .order('created_at', { ascending: false }),
        supabase
          .from('partner_payments')
          .select('*')
          .eq('partner_id', partnerId)
          .order('created_at', { ascending: false }),
        supabase
          .from('bank_information')
          .select('*')
          .eq('is_public', true)
          .order('is_primary', { ascending: false }),
      ]);

      if (invoicesRes.error?.code === '42P01' || paymentsRes.error?.code === '42P01') {
        setNeedsSetup(true);
        setLoading(false);
        return;
      }

      if (invoicesRes.data) setInvoices(invoicesRes.data);
      if (paymentsRes.data) setPayments(paymentsRes.data);
      if (bankRes.data) setBankInfo(bankRes.data);
    } catch {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceNumber = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(1000 + Math.random() * 9000);
    return `INV-${year}-${random}`;
  };

  const encodeNotesWithBank = (notes: string, bankId: string) => {
    if (!bankId) return notes;
    return `[BANK:${bankId}]${notes}`;
  };

  const decodeNotesBank = (notes: string): { bankAccountId: string | null; displayNotes: string } => {
    const match = notes?.match(/^\[BANK:([^\]]+)\]([\s\S]*)$/);
    if (match) return { bankAccountId: match[1], displayNotes: match[2] };
    return { bankAccountId: null, displayNotes: notes || '' };
  };

  const generateReceiptNumber = () => {
    const year = new Date().getFullYear();
    const random = Math.floor(1000 + Math.random() * 9000);
    return `RCT-${year}-${random}`;
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.amount || invoiceForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('partner_invoices').insert({
        partner_id: partnerId,
        invoice_number: generateInvoiceNumber(),
        amount: invoiceForm.amount,
        description: invoiceForm.description,
        due_date: invoiceForm.due_date || null,
        notes: encodeNotesWithBank(invoiceForm.notes, invoiceForm.bank_account_id),
        status: 'sent',
        issued_date: new Date().toISOString().split('T')[0],
        created_by: user?.id,
      });

      if (error) throw error;
      toast.success('Invoice created');
      setShowInvoiceForm(false);
      setInvoiceForm({
        amount: contributionAmount || 0,
        description: `${partnerTier} Sponsorship - ${partnerCompany}`,
        due_date: '',
        notes: '',
        bank_account_id: '',
      });
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoidInvoice = async (invoiceId: string) => {
    if (!confirm('Are you sure you want to void this invoice? This action cannot be undone.')) return;
    try {
      const { error } = await supabase
        .from('partner_invoices')
        .update({ status: 'voided', updated_at: new Date().toISOString() })
        .eq('id', invoiceId);
      if (error) throw error;
      toast.success('Invoice voided');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to void invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!confirm('Permanently delete this invoice? This cannot be undone.')) return;
    try {
      const { error } = await supabase
        .from('partner_invoices')
        .delete()
        .eq('id', invoiceId);
      if (error) throw error;
      toast.success('Invoice deleted');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete invoice');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || paymentForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('partner_payments').insert({
        partner_id: partnerId,
        invoice_id: paymentForm.invoice_id || null,
        amount: paymentForm.amount,
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number,
        receipt_number: generateReceiptNumber(),
        notes: paymentForm.notes,
        created_by: user?.id,
      });
      if (error) throw error;

      // Update linked invoice status to paid
      if (paymentForm.invoice_id) {
        await supabase
          .from('partner_invoices')
          .update({ status: 'paid', updated_at: new Date().toISOString() })
          .eq('id', paymentForm.invoice_id);
      }

      toast.success('Payment recorded');
      setShowPaymentForm(false);
      setPaymentForm({
        invoice_id: '',
        amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
        reference_number: '',
        notes: '',
      });
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Print</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 40px; color: #1a1a1a; }
        .header { border-bottom: 3px solid #1A472A; padding-bottom: 20px; margin-bottom: 30px; }
        .header h1 { color: #1A472A; margin: 0; font-size: 24px; }
        .header p { color: #666; margin: 4px 0 0; }
        .details { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .detail-group label { display: block; font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
        .detail-group p { margin: 0; font-size: 14px; font-weight: 500; }
        .amount-box { background: #f8f9fa; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; text-align: center; margin: 30px 0; }
        .amount-box .label { font-size: 12px; color: #888; text-transform: uppercase; }
        .amount-box .amount { font-size: 32px; font-weight: 700; color: #1A472A; }
        .bank-info { background: #fffbf5; border: 1px solid #fed7aa; border-radius: 8px; padding: 20px; margin: 20px 0; }
        .bank-info h3 { margin: 0 0 12px; font-size: 14px; color: #92400e; }
        .bank-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #fde68a; font-size: 13px; }
        .bank-row:last-child { border-bottom: none; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #888; text-align: center; }
        .status { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; }
        .status-paid { background: #dcfce7; color: #166534; }
        .status-sent { background: #dbeafe; color: #1e40af; }
        .status-overdue { background: #fef2f2; color: #991b1b; }
        @media print { body { padding: 20px; } }
      </style></head><body>
      ${printRef.current.innerHTML}
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const printInvoicePdf = (inv: Invoice) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const { bankAccountId: invBankId } = decodeNotesBank(inv.notes);
    const invoiceBanks = invBankId
      ? bankInfo.filter(b => b.id === invBankId)
      : bankInfo;

    const bankHtml = invoiceBanks.length > 0 ? `
      <div class="bank-info">
        <h3>Payment Instructions</h3>
        ${invoiceBanks.map(bank => `
          <div class="bank-row"><span>Bank Name:</span><span class="bank-val">${bank.bank_name}</span></div>
          <div class="bank-row"><span>Account Name:</span><span class="bank-val">${bank.account_name}</span></div>
          <div class="bank-row"><span>Account Number:</span><span class="bank-val">${bank.account_number}</span></div>
          ${bank.sort_code ? `<div class="bank-row"><span>Sort Code:</span><span class="bank-val">${bank.sort_code}</span></div>` : ''}
          ${bank.swift_code ? `<div class="bank-row"><span>SWIFT Code:</span><span class="bank-val">${bank.swift_code}</span></div>` : ''}
        `).join('')}
        <p class="bank-ref">Please use invoice number <strong>${inv.invoice_number}</strong> as payment reference.</p>
      </div>
    ` : '';

    printWindow.document.write(`
      <html><head><title>Invoice ${inv.invoice_number}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 48px; color: #1a1a1a; max-width: 800px; margin: 0 auto; }
        .header { border-bottom: 3px solid #1A472A; padding-bottom: 20px; margin-bottom: 32px; display: flex; justify-content: space-between; align-items: flex-start; }
        .header-left h1 { color: #1A472A; margin: 0; font-size: 28px; letter-spacing: -0.5px; }
        .header-left p { color: #666; margin: 4px 0 0; font-size: 13px; }
        .header-right { text-align: right; }
        .header-right .inv-no { font-size: 14px; font-weight: 600; color: #1A472A; }
        .header-right .inv-date { font-size: 12px; color: #666; margin-top: 4px; }
        .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
        .meta-group label { display: block; font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 4px; font-weight: 600; }
        .meta-group p { margin: 0; font-size: 14px; font-weight: 500; }
        .line-items { width: 100%; border-collapse: collapse; margin: 32px 0; }
        .line-items th { background: #1A472A; color: white; padding: 10px 16px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; text-align: left; }
        .line-items th:last-child { text-align: right; }
        .line-items td { padding: 14px 16px; border-bottom: 1px solid #e5e7eb; font-size: 14px; }
        .line-items td:last-child { text-align: right; font-weight: 600; }
        .line-items tfoot td { border-top: 2px solid #1A472A; font-weight: 700; font-size: 16px; padding-top: 16px; }
        .status { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
        .status-paid { background: #dcfce7; color: #166534; }
        .status-sent { background: #dbeafe; color: #1e40af; }
        .status-draft { background: #f3f4f6; color: #374151; }
        .status-overdue { background: #fef2f2; color: #991b1b; }
        .notes { margin: 24px 0; padding: 16px; background: #f9fafb; border-radius: 8px; }
        .notes label { font-size: 10px; color: #999; text-transform: uppercase; letter-spacing: 0.8px; font-weight: 600; }
        .notes p { margin: 6px 0 0; font-size: 13px; color: #555; }
        .bank-info { background: #fffbf5; border: 1px solid #fed7aa; border-radius: 8px; padding: 20px; margin: 24px 0; }
        .bank-info h3 { margin: 0 0 12px; font-size: 13px; color: #92400e; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .bank-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #fde68a; font-size: 13px; color: #666; }
        .bank-row:last-of-type { border-bottom: none; }
        .bank-val { font-weight: 600; color: #1a1a1a; }
        .bank-ref { font-size: 11px; color: #92400e; margin-top: 12px; }
        .footer { margin-top: 48px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #888; text-align: center; }
        @media print { body { padding: 24px; } }
      </style></head><body>
        <div class="header">
          <div class="header-left">
            <h1>INVOICE</h1>
            <p>Ibom Open Golf Tournament</p>
          </div>
          <div class="header-right">
            <div class="inv-no">${inv.invoice_number}</div>
            <div class="inv-date">Issued: ${new Date(inv.issued_date).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
        </div>

        <div class="meta">
          <div class="meta-group">
            <label>Bill To</label>
            <p>${partnerCompany}</p>
          </div>
          ${inv.due_date ? `
            <div class="meta-group">
              <label>Due Date</label>
              <p>${new Date(inv.due_date).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          ` : ''}
          <div class="meta-group">
            <label>Status</label>
            <p><span class="status status-${inv.status}">${inv.status}</span></p>
          </div>
        </div>

        <table class="line-items">
          <thead>
            <tr>
              <th>Description</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${inv.description}</td>
              <td>${new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(Number(inv.amount))}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>Total Due</td>
              <td>${new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(Number(inv.amount))}</td>
            </tr>
          </tfoot>
        </table>

        ${decodeNotesBank(inv.notes).displayNotes ? `<div class="notes"><label>Notes</label><p>${decodeNotesBank(inv.notes).displayNotes}</p></div>` : ''}
        ${bankHtml}

        <div class="footer">
          <p>Ibom Open Golf Tournament | Ibom Golf & Country Club, Uyo, Akwa Ibom State, Nigeria</p>
          <p>www.ibomopen.com</p>
        </div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: 'bg-green-100 text-green-700',
      sent: 'bg-blue-100 text-blue-700',
      draft: 'bg-gray-100 text-gray-600',
      overdue: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-500',
      voided: 'bg-red-50 text-red-500 line-through',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount), 0);
  const totalInvoiced = invoices.filter(inv => inv.status !== 'voided').reduce((sum, inv) => sum + Number(inv.amount), 0);
  const balance = totalInvoiced - totalPaid;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <ArrowPathIcon className="w-6 h-6 text-golf-orange animate-spin" />
      </div>
    );
  }

  if (needsSetup) {
    return (
      <div className="text-center py-12">
        <BanknotesIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <h4 className="text-lg font-semibold text-golf-gray mb-2">Finance Setup Required</h4>
        <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
          The invoice and payment tables need to be created before you can use this feature.
        </p>
        <button
          onClick={setupTables}
          disabled={settingUp}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-golf-green rounded-lg hover:bg-golf-green/90 transition-colors disabled:opacity-50"
        >
          {settingUp ? (
            <>
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              Setting up...
            </>
          ) : (
            'Setup Finance Tables'
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <p className="text-xs text-blue-600 font-medium uppercase">Invoiced</p>
          <p className="text-lg font-bold text-blue-900 mt-1">{formatCurrency(totalInvoiced)}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <p className="text-xs text-green-600 font-medium uppercase">Paid</p>
          <p className="text-lg font-bold text-green-900 mt-1">{formatCurrency(totalPaid)}</p>
        </div>
        <div className={`rounded-xl p-4 text-center ${balance > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
          <p className={`text-xs font-medium uppercase ${balance > 0 ? 'text-amber-600' : 'text-gray-500'}`}>Balance</p>
          <p className={`text-lg font-bold mt-1 ${balance > 0 ? 'text-amber-900' : 'text-gray-700'}`}>{formatCurrency(balance)}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setShowInvoiceForm(true)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-golf-green rounded-lg hover:bg-golf-green/90 transition-colors"
        >
          <DocumentTextIcon className="w-4 h-4" />
          Generate Invoice
        </button>
        <button
          onClick={() => setShowPaymentForm(true)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors"
        >
          <BanknotesIcon className="w-4 h-4" />
          Record Payment
        </button>
      </div>

      {/* Invoice form */}
      {showInvoiceForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h4 className="font-semibold text-golf-gray mb-4">New Invoice</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (NGN)</label>
              <input
                type="number"
                value={invoiceForm.amount || ''}
                onChange={e => setInvoiceForm(f => ({ ...f, amount: Number(e.target.value) }))}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={invoiceForm.due_date}
                onChange={e => setInvoiceForm(f => ({ ...f, due_date: e.target.value }))}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={invoiceForm.description}
                onChange={e => setInvoiceForm(f => ({ ...f, description: e.target.value }))}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={invoiceForm.notes}
                onChange={e => setInvoiceForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                placeholder="Optional notes..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account for Payment</label>
              <select
                value={invoiceForm.bank_account_id}
                onChange={e => setInvoiceForm(f => ({ ...f, bank_account_id: e.target.value }))}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              >
                <option value="">-- Select bank account --</option>
                {bankInfo.map(bank => (
                  <option key={bank.id} value={bank.id}>
                    {bank.bank_name} - {bank.account_number} ({bank.account_name})
                  </option>
                ))}
              </select>
              {bankInfo.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No bank accounts configured. Add them in Settings.</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowInvoiceForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button
              onClick={handleCreateInvoice}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-golf-green rounded-lg hover:bg-golf-green/90 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create & Send Invoice'}
            </button>
          </div>
        </div>
      )}

      {/* Payment form */}
      {showPaymentForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h4 className="font-semibold text-golf-gray mb-4">Record Payment</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (NGN)</label>
              <input
                type="number"
                value={paymentForm.amount || ''}
                onChange={e => setPaymentForm(f => ({ ...f, amount: Number(e.target.value) }))}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
              <input
                type="date"
                value={paymentForm.payment_date}
                onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select
                value={paymentForm.payment_method}
                onChange={e => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
              <input
                type="text"
                value={paymentForm.reference_number}
                onChange={e => setPaymentForm(f => ({ ...f, reference_number: e.target.value }))}
                placeholder="Bank ref / cheque number"
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
            </div>
            {invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled' && inv.status !== 'voided').length > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Invoice (optional)</label>
                <select
                  value={paymentForm.invoice_id}
                  onChange={e => {
                    const inv = invoices.find(i => i.id === e.target.value);
                    setPaymentForm(f => ({
                      ...f,
                      invoice_id: e.target.value,
                      amount: inv ? Number(inv.amount) : f.amount,
                    }));
                  }}
                  className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                >
                  <option value="">-- None --</option>
                  {invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled' && inv.status !== 'voided').map(inv => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoice_number} - {formatCurrency(Number(inv.amount))}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={paymentForm.notes}
                onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowPaymentForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button
              onClick={handleRecordPayment}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : 'Record Payment & Generate Receipt'}
            </button>
          </div>
        </div>
      )}

      {/* Invoices list */}
      {invoices.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Invoices</h4>
          <div className="space-y-2">
            {invoices.map(inv => (
              <div
                key={inv.id}
                className="flex items-center justify-between bg-white border border-gray-100 rounded-lg px-4 py-3 hover:border-gray-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{inv.invoice_number}</p>
                    <p className="text-xs text-gray-500">{inv.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-sm font-semibold text-gray-700">{formatCurrency(Number(inv.amount))}</p>
                  {statusBadge(inv.status)}
                  <button
                    onClick={() => printInvoicePdf(inv)}
                    className="p-1.5 text-gray-500 hover:text-golf-green hover:bg-green-50 rounded-lg transition-colors"
                    title="Download PDF"
                  >
                    <PrinterIcon className="w-4 h-4" />
                  </button>
                  {inv.status !== 'voided' && inv.status !== 'paid' && (
                    <button
                      onClick={() => handleVoidInvoice(inv.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Void invoice"
                    >
                      <XCircleIcon className="w-4 h-4" />
                    </button>
                  )}
                  {(inv.status === 'voided' || inv.status === 'draft') && (
                    <button
                      onClick={() => handleDeleteInvoice(inv.id)}
                      className="p-1.5 text-gray-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete invoice"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payments list */}
      {payments.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Payments & Receipts</h4>
          <div className="space-y-2">
            {payments.map(pay => (
              <div
                key={pay.id}
                className="flex items-center justify-between bg-white border border-gray-100 rounded-lg px-4 py-3 hover:border-gray-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{pay.receipt_number}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(pay.payment_date)} via {pay.payment_method.replace('_', ' ')}
                      {pay.reference_number && ` (Ref: ${pay.reference_number})`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="text-sm font-semibold text-green-700">{formatCurrency(Number(pay.amount))}</p>
                  <button
                    onClick={() => setPreviewReceipt(pay)}
                    className="p-1.5 text-gray-500 hover:text-golf-orange hover:bg-orange-50 rounded-lg transition-colors"
                    title="View / Print receipt"
                  >
                    <PrinterIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {invoices.length === 0 && payments.length === 0 && !showInvoiceForm && !showPaymentForm && (
        <div className="text-center py-8 text-gray-400">
          <BanknotesIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No invoices or payments yet. Generate an invoice to get started.</p>
        </div>
      )}

      {/* Invoice Preview / Print */}
      {previewInvoice && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewInvoice(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-golf-gray">Invoice Preview</h3>
              <div className="flex items-center gap-2">
                <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-golf-green bg-green-50 rounded-lg hover:bg-green-100">
                  <PrinterIcon className="w-4 h-4" />
                  Print
                </button>
                <button onClick={() => setPreviewInvoice(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                  <XCircleIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div ref={printRef} className="p-6">
              <div className="header">
                <h1>INVOICE</h1>
                <p>Ibom Open Golf Tournament</p>
              </div>
              <div className="details" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div className="detail-group">
                  <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Invoice No.</label>
                  <p className="text-sm font-medium">{previewInvoice.invoice_number}</p>
                </div>
                <div className="detail-group">
                  <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Date Issued</label>
                  <p className="text-sm font-medium">{formatDate(previewInvoice.issued_date)}</p>
                </div>
                <div className="detail-group">
                  <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Bill To</label>
                  <p className="text-sm font-medium">{partnerCompany}</p>
                </div>
                {previewInvoice.due_date && (
                  <div className="detail-group">
                    <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Due Date</label>
                    <p className="text-sm font-medium">{formatDate(previewInvoice.due_date)}</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-1">{previewInvoice.description}</p>
                <p className="text-2xl font-bold text-golf-green">{formatCurrency(Number(previewInvoice.amount))}</p>
              </div>

              {decodeNotesBank(previewInvoice.notes).displayNotes && (
                <div className="mb-6">
                  <p className="text-xs text-gray-400 uppercase mb-1">Notes</p>
                  <p className="text-sm text-gray-600">{decodeNotesBank(previewInvoice.notes).displayNotes}</p>
                </div>
              )}

              {(() => {
                const { bankAccountId: previewBankId } = decodeNotesBank(previewInvoice.notes);
                const previewBanks = previewBankId
                  ? bankInfo.filter(b => b.id === previewBankId)
                  : bankInfo;
                return previewBanks.length > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-semibold text-amber-800 mb-3">Payment Instructions</h3>
                  {previewBanks.map(bank => (
                    <div key={bank.id} className="space-y-1.5 mb-3 last:mb-0">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Bank Name:</span>
                        <span className="font-medium">{bank.bank_name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Account Name:</span>
                        <span className="font-medium">{bank.account_name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Account Number:</span>
                        <span className="font-medium">{bank.account_number}</span>
                      </div>
                      {bank.sort_code && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Sort Code:</span>
                          <span className="font-medium">{bank.sort_code}</span>
                        </div>
                      )}
                      {bank.swift_code && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">SWIFT Code:</span>
                          <span className="font-medium">{bank.swift_code}</span>
                        </div>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-amber-700 mt-3">
                    Please use invoice number <strong>{previewInvoice.invoice_number}</strong> as payment reference.
                  </p>
                </div>
                ) : null;
              })()}

              <div className="border-t pt-4 mt-6 text-center text-xs text-gray-400">
                <p>Ibom Open Golf Tournament | Ibom Golf & Country Club, Uyo, Akwa Ibom State, Nigeria</p>
                <p>www.ibomopen.com</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Preview / Print */}
      {previewReceipt && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setPreviewReceipt(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-golf-gray">Receipt Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setPreviewInvoice(null);
                    handlePrint();
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-golf-orange bg-orange-50 rounded-lg hover:bg-orange-100"
                >
                  <PrinterIcon className="w-4 h-4" />
                  Print
                </button>
                <button onClick={() => setPreviewReceipt(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                  <XCircleIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div ref={printRef} className="p-6">
              <div className="header">
                <h1 style={{ color: '#1A472A' }}>PAYMENT RECEIPT</h1>
                <p>Ibom Open Golf Tournament</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Receipt No.</label>
                  <p className="text-sm font-medium">{previewReceipt.receipt_number}</p>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Date</label>
                  <p className="text-sm font-medium">{formatDate(previewReceipt.payment_date)}</p>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Received From</label>
                  <p className="text-sm font-medium">{partnerCompany}</p>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Payment Method</label>
                  <p className="text-sm font-medium capitalize">{previewReceipt.payment_method.replace('_', ' ')}</p>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-center">
                <p className="text-xs text-green-600 uppercase mb-1">Amount Received</p>
                <p className="text-2xl font-bold text-green-800">{formatCurrency(Number(previewReceipt.amount))}</p>
              </div>

              {previewReceipt.reference_number && (
                <div className="mb-4">
                  <p className="text-xs text-gray-400 uppercase mb-1">Reference Number</p>
                  <p className="text-sm font-medium">{previewReceipt.reference_number}</p>
                </div>
              )}

              {previewReceipt.notes && (
                <div className="mb-4">
                  <p className="text-xs text-gray-400 uppercase mb-1">Notes</p>
                  <p className="text-sm text-gray-600">{previewReceipt.notes}</p>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <p className="text-xs text-gray-500 mb-1">For</p>
                <p className="text-sm font-medium">{partnerTier} Sponsorship - {partnerCompany}</p>
              </div>

              <div className="border-t pt-4 mt-6 text-center text-xs text-gray-400">
                <p>Ibom Open Golf Tournament | Ibom Golf & Country Club, Uyo, Akwa Ibom State, Nigeria</p>
                <p>www.ibomopen.com</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}